import React from 'react';
import { Building2, Calendar, DollarSign, Users } from 'lucide-react';
import { Deal } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface DealCardProps {
  deal: Deal;
  onClick?: () => void;
}

export default function DealCard({ deal, onClick }: DealCardProps) {
  const getStatusColor = (status: Deal['status']) => {
    switch (status) {
      case 'prospect':
        return 'bg-blue-100 text-blue-800';
      case 'initial_contact':
        return 'bg-purple-100 text-purple-800';
      case 'due_diligence':
        return 'bg-yellow-100 text-yellow-800';
      case 'negotiation':
        return 'bg-orange-100 text-orange-800';
      case 'closing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDealTypeColor = (type: Deal['type']) => {
    switch (type) {
      case 'acquisition':
        return 'bg-blue-100 text-blue-800';
      case 'merger':
        return 'bg-purple-100 text-purple-800';
      case 'divestiture':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white p-4 rounded-lg shadow-sm mb-2 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium text-gray-900">{deal.name}</h4>
            <div className="mt-1 flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(deal.status)}`}>
                {deal.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDealTypeColor(deal.type)}`}>
                {deal.type.charAt(0).toUpperCase() + deal.type.slice(1)}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center text-gray-600">
              <DollarSign className="w-4 h-4 mr-1" />
              <span className="font-medium">{formatCurrency(deal.value)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center text-gray-600">
            <Building2 className="w-4 h-4 mr-2" />
            <span className="truncate">{deal.targetCompany.name}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Users className="w-4 h-4 mr-2" />
            <span>{deal.targetCompany.employees} employees</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            <span>Close: {new Date(deal.expectedCloseDate).toLocaleDateString()}</span>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500">
              {deal.targetCompany.industry}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}